import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type {
  ICampaignAttributes,
  SmartPinpointEmailBaseCampaign,
  TPredicateMode,
} from '@feathr/blackbox';
import {
  Button,
  CardActions,
  CardContent,
  CardHeader,
  CardV2,
  Modal,
  Toggler,
} from '@feathr/components';
import FilterChip from '@feathr/extender/components/EditFilters/FilterChip';

import * as styles from './PinpointTriggersConfig.css';

interface IProps {
  campaign: SmartPinpointEmailBaseCampaign;
  disabled: boolean;
  label?: string;
}

function PinpointTriggersConfig({ campaign, disabled, label }: IProps): JSX.Element {
  const { t } = useTranslation();
  const triggers = campaign.get('actions');
  const hasMixedPredicateKinds = [...new Set(triggers.map(({ kind }) => kind))].length > 1;
  const [isConfirmingChangeMode, setIsConfirmingChangeMode] = useState(false);

  function handleChangeMode(newValue: TPredicateMode): void {
    /*
     * If we're switching from match_any to match all, we need to
     * prevent the mixing of attribute/update and activity predicates.
     */
    if (newValue === 'match_all' && hasMixedPredicateKinds && !isConfirmingChangeMode) {
      setIsConfirmingChangeMode(true);
      return;
    }

    campaign.set({ mode: newValue, actions: triggers });
  }

  function onModalCancel(): void {
    setIsConfirmingChangeMode(false);
  }

  function onModalConfirm(): void {
    // We want to reset the predicates if they are mixed between attribute and activity.
    const newTriggers = hasMixedPredicateKinds ? [triggers[0]] : triggers;
    // We need to show a confirmation modal before actually doing this.
    campaign.set({ mode: 'match_all', actions: newTriggers });
    setIsConfirmingChangeMode(false);
  }

  function handleAddTrigger(): void {
    campaign.set({
      actions: [
        ...triggers,
        {
          attr_against: undefined,
          kind: 'update',
        },
      ],
    });
  }

  function handleRemove(key: number) {
    return function () {
      campaign.set({ actions: triggers.filter((_, i) => i !== key) });
    };
  }

  const modeOptions = [
    { id: 'match_all', name: t('all') },
    { id: 'match_any', name: t('any') },
  ];

  return (
    <CardV2 width={'wide'}>
      <CardHeader title={label ?? t('Triggers')} />
      <CardContent>
        <div className={styles.header}>
          <Trans t={t}>
            Send an email to people that match{' '}
            <Toggler<ICampaignAttributes['mode']>
              className={styles.mode}
              onChange={handleChangeMode}
              options={modeOptions}
              value={campaign.get('mode')}
            />{' '}
            of the following filters:
          </Trans>
        </div>
        <div>
          {triggers.map((trigger, i) => {
            return (
              <div key={trigger.attr_against}>
                <FilterChip
                  disabled={disabled}
                  filterContext={'pinpoint'}
                  key={trigger.attr_against}
                  model={campaign}
                  onClickRemove={handleRemove(i)}
                  predicate={trigger}
                  triggers={triggers}
                />
              </div>
            );
          })}
        </div>
        {isConfirmingChangeMode && (
          <Modal
            confirmButtonType={'primary'}
            controlled={true}
            onClose={onModalCancel}
            onConfirm={onModalConfirm}
            t={t}
          >
            {t(
              'There are currently both activity and attribute predicates for this campaign. To prevent issues with deliverability, we do not allow the combination of activity and attribute predicates for email campaigns when using the all mode. Are you sure you want to continue?',
            )}
          </Modal>
        )}
      </CardContent>
      <CardActions>
        {!disabled && (
          <Button
            name={'add_filter'}
            onClick={handleAddTrigger}
            prefix={<FontAwesomeIcon icon={faPlus} />}
          >
            {t('Trigger')}
          </Button>
        )}
      </CardActions>
    </CardV2>
  );
}

export default observer(PinpointTriggersConfig);
